import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { IntegrationNamePreview } from '@modules/integrations/components/IntegrationNamePreview';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { IntegrationNameErrorMessages } from 'modules/ui/Settings/Integrations/utils/integrationErrorMessages';
import { ChangeEvent, useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { SnowflakeFormTextInputs } from './SnowflakeFormTextInputs';

export interface SnowflakeFormData {
  accountName: string;
  database: string;
  schema: string;
  warehouse: string;
  table: {
    errorMessage: null | string;
    value: string;
  };
  user: string;
  role: string;
}

const initialState: SnowflakeFormData = {
  accountName: '',
  database: '',
  schema: '',
  warehouse: '',
  table: {
    errorMessage: null,
    value: '',
  },
  user: '',
  role: '',
};

export const SnowflakeFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [formData, setFormData] = useState<SnowflakeFormData>(initialState);
  const [descriptionInput, setDescriptionInput] = useState('');

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: IntegrationChannelType.Snowflake,
    integrationType,
    createdByUser: userID,
    inputData: {
      snowflake: {
        accountName: formData.accountName,
        database: formData.database,
        schema: formData.schema,
        warehouse: formData.warehouse,
        table: formData.table.value,
        user: formData.user,
        role: formData.role,
      },
    },
    description: descriptionInput.trim(),
    onError: (error: Error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        setFormData({
          ...formData,
          table: {
            value: formData.table.value,
            errorMessage: IntegrationNameErrorMessages.DUPLICATE_NAME,
          },
        });
        return;
      }
      showErrorNotification({
        contentComponent: `Something went wrong${
          error.message ? `: ${error.message}` : '!'
        }`,
        props: {},
      });
    },
    onCompleted: () => {
      closeModal();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Snowflake,
      });
    },
  });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidated,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Snowflake,
    integrationType,
    hasValidFormFields:
      !!formData.accountName &&
      !!formData.database &&
      !!formData.schema &&
      !!formData.warehouse &&
      !!formData.table.value &&
      !formData.table.errorMessage,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      await createIntegration();
      trackIntegrationEvent({
        eventName: 'Integration Form Connect CTA Clicked',
        integrationChannelType: IntegrationChannelType.Snowflake,
      });
    },
  });

  const loading = IsLoading(status);

  const handleTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'table') {
      if (value.length > 100) {
        return setFormData({
          ...formData,
          [name]: {
            value,
            errorMessage: IntegrationNameErrorMessages.CHARACTER_LIMIT_EXCEEDED,
          },
        });
      }

      if (value.includes('-')) {
        return setFormData({
          ...formData,
          [name]: {
            value,
            errorMessage: IntegrationNameErrorMessages.DASH_UNSUPPORTED,
          },
        });
      }
      return setFormData({
        ...formData,
        [name]: {
          value,
          errorMessage: null,
        },
      });
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            <SnowflakeFormTextInputs
              formData={formData}
              handleTextInputChange={handleTextInputChange}
              isLoading={loading}
            />
            <IntegrationNamePreview
              integrationName={formData.table.value}
              sourceName={IntegrationChannelType.Snowflake}
            />
          </Stack>
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidated={isPreValidated}
            integrationType={integrationType}
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText="Integrate"
        isDisabled={isDisabled}
        isLoading={loading}
      />
    </Stack>
  );
};
